'use client';

/**
 * Button — v4 primitive.
 * Variants: primary (emerald gradient), violet, ghost. Size: sm.
 * Framer scale-on-press. Loading state shows a spinner and disables input.
 */

import { motion } from 'framer-motion';
import type { ReactNode } from 'react';
import { pressScale } from '../theme/motion';

export type ButtonVariant = 'primary' | 'violet' | 'ghost';

export interface ButtonProps
  extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    | 'className'
    | 'style'
    | 'color'
    | 'onAnimationStart'
    | 'onAnimationEnd'
    | 'onDrag'
    | 'onDragStart'
    | 'onDragEnd'
  > {
  variant?: ButtonVariant;
  sm?: boolean;
  loading?: boolean;
  /** Optional leading icon (e.g. a lucide-react icon element). */
  leftIcon?: ReactNode;
  /** Optional trailing icon (e.g. an arrow). */
  rightIcon?: ReactNode;
  children?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: 'v4-btn--primary',
  violet: 'v4-btn--violet',
  ghost: 'v4-btn--ghost',
};

export function Button({
  variant = 'primary',
  sm = false,
  loading = false,
  leftIcon,
  rightIcon,
  children,
  disabled,
  className,
  style,
  ...rest
}: ButtonProps) {
  const classes = [
    'v4-btn',
    VARIANT_CLASS[variant],
    sm ? 'v4-btn--sm' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <motion.button
      {...pressScale}
      className={classes}
      style={style}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <span className="v4-spinner" aria-hidden />
      ) : (
        leftIcon
      )}
      {children}
      {!loading && rightIcon}
    </motion.button>
  );
}

export default Button;
